import React, { useState, useEffect } from 'react';
import axios from 'axios';
import brandPersonas from '../data/brandPersonas.json';
import { useOrgBrand } from '../context/OrgBrandContext';
import VectorIcon from '../icons/Vector.png';
import '../styles/BrandVoiceDesigner.css';

const BrandVoiceDesigner = ({ user }) => {
  const { selectedOrganization, selectedBrand } = useOrgBrand();
  const [screen, setScreen] = useState('initializing'); // initializing, url-input, url-selection, loading, tournament, results
  const [urlInput, setUrlInput] = useState('');
  const [userName, setUserName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [error, setError] = useState(null);
  const [suggestedUrls, setSuggestedUrls] = useState([]);
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [selectedUrlTitle, setSelectedUrlTitle] = useState(null);
  const [isExtractingUrls, setIsExtractingUrls] = useState(false);

  // Phase 3: AI-generated questions state
  const [generatedQuestions, setGeneratedQuestions] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [questionMetadata, setQuestionMetadata] = useState(null);

  // Phase 4: Progress tracking state
  const [brandId, setBrandId] = useState(null);
  const [completionId, setCompletionId] = useState(null);
  const [completionCount, setCompletionCount] = useState(0);
  const [retakeAllowed, setRetakeAllowed] = useState(true);
  const [isApproved, setIsApproved] = useState(false);
  const [completedBy, setCompletedBy] = useState(null);

  // Phase 4: Back button state
  const [previousRound, setPreviousRound] = useState(null);
  const [canGoBack, setCanGoBack] = useState(false);

  // Tournament state
  const [tournamentState, setTournamentState] = useState({
    round: 1,
    winners: {},
    brandUrl: '',
    finalWinner: null,
    allPersonas: {}
  });

  // Timer state for progressive generation
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [canProceed, setCanProceed] = useState(false);
  const [isGeneratingNext, setIsGeneratingNext] = useState(false);
  const MIN_READ_TIME = 5; // 5 seconds minimum

  // Static test questions for Phase 2 (fallback)
  const staticQuestions = [
    {
      round: 1,
      question: "How does your brand communicate with customers?",
      optionA_post: "We're here to help you grow. Our proven strategies deliver results that matter. Let's build something amazing together.",
      optionB_post: "Real talk: marketing is hard. We get it. That's why we're obsessed with making your life easier. DM us!"
    },
    {
      round: 2,
      question: "What's your brand's approach to problem-solving?",
      optionA_post: "Challenges are opportunities. Our systematic approach ensures every problem becomes a breakthrough moment.",
      optionB_post: "Got a problem? Let's tackle it together. No corporate speak, just real solutions that actually work."
    },
    {
      round: 3,
      question: "How does your brand express creativity?",
      optionA_post: "Innovation meets strategy. We blend creative thinking with data-driven insights to deliver exceptional outcomes.",
      optionB_post: "Creativity isn't just our job—it's our obsession. We think differently because different gets noticed."
    },
    {
      round: 4,
      question: "What tone does your brand take with challenges?",
      optionA_post: "Every challenge is met with expertise and professionalism. Our track record speaks for itself.",
      optionB_post: "Bring on the tough stuff! We love a good challenge. Makes the win that much sweeter."
    },
    {
      round: 5,
      question: "How does your brand build relationships?",
      optionA_post: "Long-term partnerships built on trust, transparency, and consistent delivery of excellence.",
      optionB_post: "We're not just vendors, we're your partners in crime. Let's grow together."
    },
    {
      round: 6,
      question: "What's your brand's communication style?",
      optionA_post: "Clear, professional communication ensures alignment at every stage of our partnership.",
      optionB_post: "No jargon, no fluff. Just straight talk about what works and what doesn't."
    },
    {
      round: 7,
      question: "How does your brand approach growth?",
      optionA_post: "Strategic planning and methodical execution drive sustainable growth for our clients.",
      optionB_post: "Growth hacking, experimenting, learning fast. That's how we roll."
    },
    {
      round: 8,
      question: "What values drive your brand?",
      optionA_post: "Excellence, integrity, and results. These principles guide everything we do.",
      optionB_post: "Authenticity wins. We keep it real, stay curious, and never stop improving."
    },
    {
      round: 9,
      question: "How refined is your brand's voice?",
      optionA_post: "Polished and professional. Our communications reflect our commitment to quality.",
      optionB_post: "Refined but relatable. We sound smart without being stuffy."
    },
    {
      round: 10,
      question: "Which direction feels most authentic?",
      optionA_post: "Established expertise you can trust. Professional service, proven results.",
      optionB_post: "Down-to-earth and real. We're the team you actually want to work with."
    }
  ];

  // Adjacency map for determining Q+ and Q-
  const adjacencyMap = {
    'A': { prev: null, next: 'Ab' },
    'Ab': { prev: 'A', next: 'B' },
    'B': { prev: 'Ab', next: 'Bc' },
    'Bc': { prev: 'B', next: 'C' },
    'C': { prev: 'Bc', next: 'Cd' },
    'Cd': { prev: 'C', next: 'D' },
    'D': { prev: 'Cd', next: 'De' },
    'De': { prev: 'D', next: 'E' },
    'E': { prev: 'De', next: 'Ef' },
    'Ef': { prev: 'E', next: 'F' },
    'F': { prev: 'Ef', next: 'Fg' },
    'Fg': { prev: 'F', next: 'G' },
    'G': { prev: 'Fg', next: 'Gh' },
    'Gh': { prev: 'G', next: 'H' },
    'H': { prev: 'Gh', next: 'Hi' },
    'Hi': { prev: 'H', next: 'I' },
    'I': { prev: 'Hi', next: null }
  };

  // Generate adjacent persona (blend of two personas)
  const generateAdjacentPersona = (persona1Id, persona2Id, label) => {
    const p1 = brandPersonas[persona1Id];
    const p2 = brandPersonas[persona2Id];

    if (!p1 || !p2) return null;

    // Determine weighting based on label direction
    // e.g., "Ab" = 70% A + 30% B, "Ba" would be 30% A + 70% B
    // For simplicity, we'll do 50/50 blend for now

    return {
      id: label,
      name: `${p1.name} / ${p2.name}`,
      keywords: [...p1.keywords.slice(0, 2), p2.keywords[0]],
      explainer: `A balanced voice that combines a ${p1.keywords[0].toLowerCase()} approach with a ${p2.keywords[0].toLowerCase()} nature.`,
      traits: [
        ...p1.traits.slice(0, 2),
        ...p2.traits.slice(0, 2)
      ],
      guidelines: [
        ...p1.guidelines.slice(0, 4),
        ...p2.guidelines.slice(0, 3)
      ],
      introduction: `This hybrid voice merges two complementary communication styles, creating a unique blend that balances both approaches.`
    };
  };

  // Initialize all personas (main + adjacent) when component mounts
  useEffect(() => {
    const allPersonas = { ...brandPersonas };

    // Generate all adjacent personas
    const adjacentPairs = [
      ['A', 'B', 'Ab'],
      ['B', 'C', 'Bc'],
      ['C', 'D', 'Cd'],
      ['D', 'E', 'De'],
      ['E', 'F', 'Ef'],
      ['F', 'G', 'Fg'],
      ['G', 'H', 'Gh'],
      ['H', 'I', 'Hi']
    ];

    adjacentPairs.forEach(([p1, p2, label]) => {
      const adjacent = generateAdjacentPersona(p1, p2, label);
      if (adjacent) {
        allPersonas[label] = adjacent;
      }
    });

    setTournamentState(prev => ({ ...prev, allPersonas }));
  }, []);

  // Phase 4: Load progress when brand is selected
  useEffect(() => {
    if (selectedBrand) {
      loadProgress();
    } else {
      // No brand selected, show message
      setScreen('url-input');
    }
  }, [selectedBrand]);

  // Set brand name from selected brand when it changes
  useEffect(() => {
    if (selectedBrand && !brandName) {
      setBrandName(selectedBrand.name || selectedBrand.brand_name || '');
    }
  }, [selectedBrand]);

  // Phase 4: Add keyboard event listener
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (screen === 'tournament' && canProceed) {
        if (e.key === '1') {
          handleSelection('A');
        } else if (e.key === '2') {
          handleSelection('B');
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [screen, tournamentState.round, canProceed]);

  // Timer useEffect - counts up and enables proceeding after MIN_READ_TIME
  useEffect(() => {
    let interval;
    if (isTimerActive && screen === 'tournament') {
      interval = setInterval(() => {
        setTimeElapsed(prev => {
          const newTime = prev + 1;
          if (newTime >= MIN_READ_TIME && !canProceed) {
            setCanProceed(true);
            setIsTimerActive(false);
          }
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerActive, screen, canProceed]);

  // Phase 4: Load progress function
  const loadProgress = async () => {
    // Get brand_id from selected brand in context
    const userBrandId = selectedBrand?._id || selectedBrand?.id;
    if (!userBrandId) {
      console.log('No brand selected');
      setScreen('url-input');
      return;
    }

    setBrandId(userBrandId);

    try {
      const response = await axios.get(`/api/brand-voice/progress?brand_id=${userBrandId}`, { withCredentials: true });

      if (response.data.success && response.data.progress) {
        const progress = response.data.progress;

        // Restore state
        setCompletionId(progress._id);
        setCompletionCount(progress.completion_count || 0);
        setRetakeAllowed(progress.retake_allowed);
        setIsApproved(progress.status === 'approved' && progress.is_active);

        // Store who completed it
        if (progress.completed_by_name || progress.completed_by_email) {
          setCompletedBy({
            name: progress.completed_by_name,
            email: progress.completed_by_email
          });
        }

        if (progress.status === 'in_progress') {
          // Resume where they left off
          const savedQuestions = progress.generated_questions || [];
          const currentRound = progress.current_round || 1;

          // Check if the question for current round exists
          if (savedQuestions.length >= currentRound) {
            // Safe to resume
            setGeneratedQuestions(savedQuestions);
            setUrlInput(progress.brand_url);
            setTournamentState(prev => ({
              ...prev,
              round: currentRound,
              winners: progress.winners || {},
              brandUrl: progress.brand_url,
              finalWinner: null
            }));
            setScreen('tournament');
          } else {
            // Questions incomplete - show error and allow restart
            console.error(`Progress says round ${currentRound} but only ${savedQuestions.length} questions exist`);
            setError('Your previous session was incomplete. Please start over.');
            setScreen('url-input');
          }
        } else if (progress.status === 'completed' || progress.status === 'approved' || progress.status === 'rejected') {
          // Show results screen for completed, approved, and rejected
          setGeneratedQuestions(progress.generated_questions);
          setUrlInput(progress.brand_url);
          setTournamentState(prev => ({
            ...prev,
            round: 10,
            winners: progress.winners || {},
            brandUrl: progress.brand_url,
            finalWinner: progress.final_winner
          }));
          setScreen('results');
        } else {
          // No active progress, show URL input
          setScreen('url-input');
        }
      } else {
        // No progress found, show URL input
        setScreen('url-input');
      }
    } catch (error) {
      console.error('Error loading progress:', error);
      setScreen('url-input');
    }
  };

  // Phase 4: Save progress function
  const saveProgress = async (updatedState, status = 'in_progress') => {
    if (!brandId) return;

    try {
      await axios.post('/api/brand-voice/save-progress', {
        brand_id: brandId,
        status: status,
        current_round: updatedState.round,
        brand_url: updatedState.brandUrl,
        generated_questions: generatedQuestions,
        winners: updatedState.winners,
        final_winner: updatedState.finalWinner
      }, { withCredentials: true });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  // Generate ONLY fixed pairings for rounds 1-4 (initial generation)
  const getInitialPairings = () => {
    const initialPairings = [];
    // Rounds 1-4: Fixed pairings (always needed)
    initialPairings.push([1, 'A', 'B']);
    initialPairings.push([2, 'C', 'D']);
    initialPairings.push([3, 'E', 'F']);
    initialPairings.push([4, 'G', 'H']);
    return initialPairings;
  };

  // Call 2: Generate blended voices for rounds 7-10
  const generateBlendedVoices = async () => {
    // Only generate once
    if (isGeneratingNext) return;

    try {
      setIsGeneratingNext(true);
      const blendedVoiceIds = ['Ab', 'Bc', 'Cd', 'De', 'Ef', 'Fg', 'Gh', 'Hi'];

      const response = await axios.post('/api/brand-voice/generate-questions', {
        url: tournamentState.brandUrl,
        voice_ids: blendedVoiceIds,
        cached_content: questionMetadata
      }, { withCredentials: true });

      if (response.data.success) {
        // Merge blended posts into existing voice posts
        setVoicePosts((prev) => ({
          ...prev,
          ...response.data.posts
        }));
        console.log(`Generated ${Object.keys(response.data.posts).length} blended voice posts`);
      }
    } catch (error) {
      console.error('Error generating blended voices:', error);
    } finally {
      setIsGeneratingNext(false);
    }
  };

  // Pre-generate both possible paths for a specific final round (9 or 10)
  const pregenerateFinalRound = async (round8Pairing, roundNumber) => {
    if (!round8Pairing) return;

    const { optionA, optionB } = round8Pairing;
    const pairingsToGenerate = [];

    if (roundNumber === 9) {
      // Generate both possible round 9s
      const optionA_next = adjacencyMap[optionA]?.next;
      const optionB_next = adjacencyMap[optionB]?.next;

      if (optionA_next) {
        pairingsToGenerate.push([9, optionA, optionA_next]);
      }
      if (optionB_next) {
        pairingsToGenerate.push([9, optionB, optionB_next]);
      }
    } else if (roundNumber === 10) {
      // Generate both possible round 10s
      const optionA_prev = adjacencyMap[optionA]?.prev;
      const optionB_prev = adjacencyMap[optionB]?.prev;

      if (optionA_prev) {
        pairingsToGenerate.push([10, optionA, optionA_prev]);
      }
      if (optionB_prev) {
        pairingsToGenerate.push([10, optionB, optionB_prev]);
      }
    }

    if (pairingsToGenerate.length === 0) return;

    try {
      console.log(`Pre-generating round ${roundNumber} for both paths:`, pairingsToGenerate);
      const response = await axios.post('/api/brand-voice/generate-questions', {
        url: tournamentState.brandUrl,
        custom_pairings: pairingsToGenerate,
        cached_content: questionMetadata
      }, { withCredentials: true });

      if (response.data.success && response.data.questions.length > 0) {
        setGeneratedQuestions((prev) => {
          if (!prev) return response.data.questions;

          const newQuestions = response.data.questions.filter(newQ => {
            return !prev.some(existingQ =>
              existingQ.round === newQ.round &&
              existingQ.avatarA === newQ.avatarA &&
              existingQ.avatarB === newQ.avatarB
            );
          });

          return [...prev, ...newQuestions].sort((a, b) => a.round - b.round);
        });
        console.log(`Pre-generated ${response.data.questions.length} round ${roundNumber} questions`);
      }
    } catch (error) {
      console.error(`Error pre-generating round ${roundNumber}:`, error);
    }
  };

  // Get tournament pairing for current round
  const getTournamentPairing = (round, winners) => {
    const pairings = {
      1: { optionA: 'A', optionB: 'B' },
      2: { optionA: 'C', optionB: 'D' },
      3: { optionA: 'E', optionB: 'F' },
      4: { optionA: 'G', optionB: 'H' },
      5: { optionA: 'I', optionB: winners[1] || 'A' }, // I vs J (winner of round 1)
      6: { optionA: winners[2] || 'C', optionB: winners[3] || 'E' }, // K vs L
      7: { optionA: winners[4] || 'G', optionB: winners[5] || 'I' }, // M vs N
      8: { optionA: winners[6] || 'C', optionB: winners[7] || 'G' }, // O vs P (championship)
    };

    // Rounds 9-10 use winners and adjacent personas
    if (round === 9) {
      const Q = winners[8]; // Round 8 winner
      // Try next first, fallback to prev for edge cases (e.g., I has no next, use Hi)
      const Qplus = Q ? (adjacencyMap[Q]?.next || adjacencyMap[Q]?.prev) : null;
      if (!Qplus) {
        // No adjacent voice available
        return null;
      }
      return { optionA: Q, optionB: Qplus };
    }

    if (round === 10) {
      const Q = winners[9]; // Round 9 winner

      // Calculate what Round 9's opponent was (to avoid duplicates)
      const round9Winner = winners[8];
      const round9Opponent = round9Winner ? (adjacencyMap[round9Winner]?.next || adjacencyMap[round9Winner]?.prev) : null;

      // Try prev first
      let Qminus = Q ? adjacencyMap[Q]?.prev : null;

      // If prev is null or would duplicate Round 9, find alternative
      if (!Qminus || (Qminus === round9Opponent && Q === round9Winner)) {
        // Edge case handling for voices A and I
        if (Q === 'A') {
          // Voice A: prev is null, next would be Ab (duplicate of R9)
          // Skip to B instead
          Qminus = 'B';
        } else if (Q === 'I') {
          // Voice I: prev is Hi (duplicate of R9)
          // Skip to H instead
          Qminus = 'H';
        } else if (Qminus === round9Opponent) {
          // Would duplicate Round 9, skip one more in the chain
          Qminus = Qminus ? adjacencyMap[Qminus]?.prev : null;
          if (!Qminus) {
            Qminus = Q ? adjacencyMap[Q]?.next : null;
          }
        } else {
          // Regular fallback to next
          Qminus = Q ? adjacencyMap[Q]?.next : null;
        }
      }

      if (!Qminus) {
        // No adjacent voice available
        return null;
      }

      return { optionA: Q, optionB: Qminus };
    }

    return pairings[round];
  };

  // Handle initial URL submission - Extract URLs for selection
  const handleAnalyzeUrl = async () => {
    if (!userName.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!brandName.trim()) {
      setError('Please enter your brand name');
      return;
    }

    if (!urlInput.trim()) {
      setError('Please enter a website URL');
      return;
    }

    // Auto-add https:// if no protocol is present
    let originalUrl = urlInput.trim();
    if (!originalUrl.startsWith('http://') && !originalUrl.startsWith('https://')) {
      originalUrl = 'https://' + originalUrl;
    }

    let parsedUrl;
    try {
      parsedUrl = new URL(originalUrl);
    } catch (e) {
      setError('Please enter a valid URL');
      return;
    }

    setError(null);
    setIsExtractingUrls(true);

    try {
      // Always scrape the base domain (homepage) to get navigation links
      const baseDomain = `${parsedUrl.protocol}//${parsedUrl.hostname}`;

      // Extract suggested URLs from the base domain
      const response = await axios.post('/api/brand-voice/extract-urls', {
        url: baseDomain,
        max_urls: 5
      }, { withCredentials: true });

      if (response.data.success && response.data.urls && response.data.urls.length > 0) {
        // Always include the user's original URL as the first option
        const urls = [
          { url: originalUrl, title: 'Original URL' }
        ];

        // Add suggested URLs from homepage, but skip if it's the same as original or base domain
        response.data.urls.forEach(urlObj => {
          const normalizedUrl = urlObj.url.toLowerCase().replace(/\/$/, '');
          const normalizedOriginal = originalUrl.toLowerCase().replace(/\/$/, '');
          const normalizedBase = baseDomain.toLowerCase().replace(/\/$/, '');

          // Only add if it's different from the original URL and not already in the list
          if (normalizedUrl !== normalizedOriginal &&
              normalizedUrl !== normalizedBase &&
              !urls.some(u => u.url.toLowerCase().replace(/\/$/, '') === normalizedUrl)) {
            urls.push(urlObj);
          }
        });

        // If base domain is different from original, add it as "Homepage"
        if (baseDomain.toLowerCase() !== originalUrl.toLowerCase().replace(/\/$/, '')) {
          urls.push({ url: baseDomain, title: 'Homepage' });
        }

        setSuggestedUrls(urls);
        setScreen('url-selection');
      } else {
        // If extraction fails, proceed with original URL
        setSuggestedUrls([{ url: originalUrl, title: 'Original URL' }]);
        setScreen('url-selection');
      }
      setIsExtractingUrls(false);
    } catch (error) {
      console.error('Error extracting URLs:', error);
      // If error, just proceed with original URL
      setSuggestedUrls([{ url: originalUrl, title: 'Original URL' }]);
      setScreen('url-selection');
      setIsExtractingUrls(false);
    }
  };

  // NEW: State to store posts by voice ID
  const [voicePosts, setVoicePosts] = useState({});

  // Handle final URL selection and start generation
  const handleUrlSelection = async (url, title) => {
    setSelectedUrl(url);
    setSelectedUrlTitle(title || 'Selected Page');
    setError(null);
    setIsGenerating(true);
    setScreen('loading');

    try {
      // Call 1: Generate posts for base voices (A-I) - covers rounds 1-6
      const baseVoiceIds = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];

      const response = await axios.post('/api/brand-voice/generate-questions', {
        url: url,
        voice_ids: baseVoiceIds
      }, { withCredentials: true });

      if (response.data.success) {
        // Store posts by voice ID
        setVoicePosts(response.data.posts);
        setQuestionMetadata(response.data.scraped_content);

        // Initialize tournament state
        setTournamentState(prev => ({
          ...prev,
          brandUrl: url,
          round: 1,
          winners: {},
          finalWinner: null
        }));

        // Start timer for round 1
        setTimeElapsed(0);
        setIsTimerActive(true);
        setCanProceed(false);

        setScreen('tournament');
        setIsGenerating(false);
      } else {
        setError(response.data.error || 'Failed to generate questions');
        setScreen('url-input');
        setIsGenerating(false);
      }
    } catch (error) {
      console.error('Error generating questions:', error);

      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.code === 'ECONNABORTED') {
        setError('Request timed out. Please try again.');
      } else if (!error.response) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Failed to generate questions. Please try again.');
      }

      setScreen('url-input');
      setIsGenerating(false);
    }
  };

  // Generate a single question for a specific round with exact avatar pairing
  const generateQuestionForRound = async (round, pairing) => {
    try {
      // Custom pairing: [(round, avatarA, avatarB)]
      const customPairings = [[round, pairing.optionA, pairing.optionB]];

      const response = await axios.post('/api/brand-voice/generate-questions', {
        url: tournamentState.brandUrl,
        start_round: round,
        end_round: round,
        cached_content: questionMetadata,
        custom_pairings: customPairings
      }, { withCredentials: true });

      if (response.data.success) {
        const newQuestion = response.data.questions[0];

        setGeneratedQuestions((prev) => {
          if (!prev) return [newQuestion];
          // Add new question and sort
          return [...prev, newQuestion].sort((a, b) => a.round - b.round);
        });
      } else {
        throw new Error(response.data.error || 'Failed to generate question');
      }
    } catch (error) {
      console.error(`Error generating question for round ${round}:`, error);
      throw error;
    }
  };

  // Handle option selection in tournament
  const handleSelection = async (choice) => {
    // Prevent selection if timer hasn't completed
    if (!canProceed) return;

    const pairing = getTournamentPairing(tournamentState.round, tournamentState.winners);

    if (!pairing) {
      // No valid pairing, tournament is complete
      const finalWinner = tournamentState.winners[tournamentState.round - 1] || tournamentState.winners[8];
      const updatedState = { ...tournamentState, finalWinner };
      setTournamentState(updatedState);
      setScreen('loading');
      try {
        await completeDesigner(finalWinner);
        setScreen('results');
      } catch (error) {
        setError('Failed to save your results. Please try again.');
        setScreen('tournament');
      }
      return;
    }

    const winner = choice === 'A' ? pairing.optionA : pairing.optionB;
    const newWinners = { ...tournamentState.winners, [tournamentState.round]: winner };
    const nextRound = tournamentState.round + 1;

    // Save previous state for back button
    setPreviousRound({
      round: tournamentState.round,
      winners: tournamentState.winners
    });
    setCanGoBack(true);

    // Check if we're done
    if (nextRound > 10) {
      const updatedState = {
        ...tournamentState,
        winners: newWinners,
        finalWinner: winner
      };
      setTournamentState(updatedState);
      setScreen('loading');
      try {
        await completeDesigner(winner);
        setScreen('results');
      } catch (error) {
        setError('Failed to save your results. Please try again.');
        setScreen('tournament');
      }
      return;
    }

    // Check if next pairing exists
    const nextPairing = getTournamentPairing(nextRound, newWinners);
    if (!nextPairing) {
      // No next pairing, we're done
      const updatedState = {
        ...tournamentState,
        winners: newWinners,
        finalWinner: winner
      };
      setTournamentState(updatedState);
      setScreen('loading');
      try {
        await completeDesigner(winner);
        setScreen('results');
      } catch (error) {
        setError('Failed to save your results. Please try again.');
        setScreen('tournament');
      }
      return;
    }

    // Update UI and reset timer for next round
    const updatedState = {
      ...tournamentState,
      round: nextRound,
      winners: newWinners
    };
    setTournamentState(updatedState);

    // Reset timer for next round
    setTimeElapsed(0);
    setCanProceed(false);
    setIsTimerActive(true);

    // Call 2: Trigger after round 2 to generate blended voices for rounds 7-10
    if (nextRound === 3) {
      generateBlendedVoices();
    }

    // Save progress in background (non-blocking)
    saveProgress(updatedState);
  };

  // Phase 4: Back button handler
  const handleBack = () => {
    if (!canGoBack || !previousRound) return;

    setTournamentState(prev => ({
      ...prev,
      round: previousRound.round,
      winners: previousRound.winners
    }));
    setCanGoBack(false);
    setPreviousRound(null);
  };

  // Phase 4: Complete designer
  const completeDesigner = async (finalWinner) => {
    if (!brandId) {
      throw new Error('No brand ID');
    }

    const finalPersona = getPersona(finalWinner);

    try {
      const response = await axios.post('/api/brand-voice/complete', {
        brand_id: brandId,
        brand_url: urlInput,
        generated_questions: generatedQuestions,
        winners: tournamentState.winners,
        final_winner: finalWinner,
        persona_data: finalPersona
      }, { withCredentials: true });

      if (response.data.success) {
        setCompletionCount(response.data.completion_count);
        setRetakeAllowed(response.data.retake_allowed);
      } else {
        throw new Error(response.data.error || 'Failed to save completion');
      }
    } catch (error) {
      console.error('Error completing designer:', error);
      throw error;
    }
  };

  // Phase 4: Retake handler
  const handleRetake = () => {
    // Reset to URL input (unlimited retakes allowed)
    setScreen('url-input');
    setUrlInput('');
    setUserName('');
    setBrandName('');
    setError(null);
    setGeneratedQuestions(null);
    setSuggestedUrls([]);
    setSelectedUrl(null);
    setCompletedBy(null);
    setTournamentState(prev => ({
      ...prev,
      round: 1,
      winners: {},
      brandUrl: '',
      finalWinner: null
    }));
    setPreviousRound(null);
    setCanGoBack(false);
  };

  // Get persona by ID
  const getPersona = (personaId) => {
    return tournamentState.allPersonas[personaId] || brandPersonas[personaId];
  };

  // Map avatar ID to Brand Voice Type number
  const getVoiceTypeNumber = (personaId) => {
    const mapping = {
      'A': '1', 'Ab': '1-2', 'B': '2', 'Bc': '2-3', 'C': '3', 'Cd': '3-4',
      'D': '4', 'De': '4-5', 'E': '5', 'Ef': '5-6', 'F': '6', 'Fg': '6-7',
      'G': '7', 'Gh': '7-8', 'H': '8', 'Hi': '8-9', 'I': '9'
    };
    return mapping[personaId] || '1';
  };

  // Get current question based on round and actual pairing
  const getCurrentQuestion = () => {
    const pairing = getTournamentPairing(tournamentState.round, tournamentState.winners);
    if (!pairing) return null;

    // Get posts for the two voices in this matchup
    const postA = voicePosts[pairing.optionA];
    const postB = voicePosts[pairing.optionB];

    // If posts not available yet, return null (show loading)
    if (!postA || !postB) {
      console.warn(`Posts not available for round ${tournamentState.round}: ${pairing.optionA} vs ${pairing.optionB}`);
      console.warn(`Available voices:`, Object.keys(voicePosts));
      return null;
    }

    // Return question object with posts mapped to current pairing
    return {
      round: tournamentState.round,
      avatarA: pairing.optionA,
      avatarB: pairing.optionB,
      question: `Which option feels more like ${brandName}?`,
      optionA_post: postA,
      optionB_post: postB
    };
  };

  // Render loading screen
  const renderLoadingScreen = () => {
    const isFinalSave = tournamentState.round === 10 && tournamentState.finalWinner;

    return (
      <div className="bvd-loading-screen">
        <div className="bvd-loading-spinner"></div>
        <h2>{isFinalSave ? 'Saving Your Results' : 'Analyzing Your Brand'}</h2>
        <p>{isFinalSave ? 'Please wait while we save your brand voice...' : 'We\'re reading your website and generating personalized questions...'}</p>
      </div>
    );
  };

  // Render tournament screen
  const renderTournamentScreen = () => {
    const pairing = getTournamentPairing(tournamentState.round, tournamentState.winners);

    if (!pairing) {
      return <div>Tournament complete!</div>;
    }

    const personaA = getPersona(pairing.optionA);
    const personaB = getPersona(pairing.optionB);
    const currentQuestion = getCurrentQuestion();

    // Show loading state if current question is not ready yet
    if (!currentQuestion) {
      const totalVoices = Object.keys(voicePosts).length;

      return (
        <div className="bvd-tournament-screen">
          <div className="bvd-tournament-header">
            <div className="bvd-progress-indicator">
              Round {tournamentState.round} of 10
            </div>
            <div className="bvd-progress-dots">
              {[...Array(10)].map((_, i) => (
                <div
                  key={i}
                  className={`dot ${
                    i < tournamentState.round - 1
                      ? 'completed'
                      : i === tournamentState.round - 1
                      ? 'active'
                      : ''
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="bvd-loading-screen">
            <div className="bvd-loading-spinner"></div>
            <h2>Loading Next Question</h2>
            <p>Preparing round {tournamentState.round}...</p>
            {totalVoices < 9 && (
              <div style={{ marginTop: '20px' }}>
                <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                  Generated {totalVoices} of 17 voice posts
                </p>
                <button
                  className="bvd-secondary-button"
                  onClick={handleRetake}
                  style={{ marginTop: '10px' }}
                >
                  Start Over
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    const progressPercentage = (tournamentState.round / 10) * 100;

    return (
      <div className="bvd-tournament-screen">
        {/* Top header with title and progress */}
        <div className="bvd-tournament-top-header">
          <div className="bvd-tournament-title">
            <img src={VectorIcon} alt="" className="bvd-tournament-title-icon" />
            BRAND VOICE DESIGNER
          </div>
          <div className="bvd-tournament-step">{String(tournamentState.round).padStart(2, '0')} / 10</div>
        </div>

        {/* Progress bar */}
        <div className="bvd-progress-bar-container">
          <div className="bvd-progress-bar-fill" style={{ width: `${progressPercentage}%` }}></div>
        </div>

        {/* Question */}
        <h2 className="bvd-question-text">
          Which option feels more like {brandName}?
        </h2>

        {/* A/B comparison cards */}
        <div className="bvd-comparison-cards">
          <div
            className={`bvd-option-card ${!canProceed ? 'bvd-option-disabled' : ''}`}
            onClick={() => handleSelection('A')}
          >
            <div className="bvd-option-letter">A</div>
            {currentQuestion?.optionA_post ? (
              <p className="bvd-option-text">{currentQuestion.optionA_post}</p>
            ) : personaA && (
              <p className="bvd-option-text">{personaA.explainer}</p>
            )}
          </div>

          <span className="bvd-or-divider">or</span>

          <div
            className={`bvd-option-card ${!canProceed ? 'bvd-option-disabled' : ''}`}
            onClick={() => handleSelection('B')}
          >
            <div className="bvd-option-letter">B</div>
            {currentQuestion?.optionB_post ? (
              <p className="bvd-option-text">{currentQuestion.optionB_post}</p>
            ) : personaB && (
              <p className="bvd-option-text">{personaB.explainer}</p>
            )}
          </div>
        </div>

        {/* Context capsule */}
        <div className="bvd-context-capsule">
          <svg className="bvd-context-globe-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="2" y1="12" x2="22" y2="12"></line>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
          </svg>
          <span className="bvd-context-url">{urlInput.replace(/^https?:\/\//, '')}</span>
          <div className="bvd-context-separator"></div>
          <span className="bvd-context-page">Context: {selectedUrlTitle || 'Selected Page'}</span>
        </div>

        {/* Timer indicator */}
        {!canProceed && (
          <div className="bvd-timer-indicator">
            <div className="bvd-timer-text">
              Read carefully... ({MIN_READ_TIME - timeElapsed}s remaining)
            </div>
            <div className="bvd-timer-bar">
              <div
                className="bvd-timer-bar-fill"
                style={{ width: `${(timeElapsed / MIN_READ_TIME) * 100}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render results screen
  const renderResultsScreen = () => {
    const finalPersona = getPersona(tournamentState.finalWinner);

    if (!finalPersona) {
      return <div>No persona selected</div>;
    }

    return (
      <div className="bvd-results-screen">
        {/* Top header with title and progress - matching tournament layout */}
        <div className="bvd-tournament-top-header">
          <div className="bvd-tournament-title">
            <img src={VectorIcon} alt="" className="bvd-tournament-title-icon" />
            BRAND VOICE DESIGNER
          </div>
          <div className="bvd-tournament-step">10 / 10</div>
        </div>

        {/* Progress bar - 100% complete */}
        <div className="bvd-progress-bar-container">
          <div className="bvd-progress-bar-fill" style={{ width: '100%' }}></div>
        </div>

        {/* Centered content container - 943px width */}
        <div className="bvd-results-content-container">
          {/* Main title */}
          <h1 className="bvd-results-main-title">Introducing the voice of your brand:</h1>

          {/* Explainer text */}
          <p className="bvd-results-explainer">
            {finalPersona.explainer}
          </p>

          {/* Two column layout for PERSONA and KEY TRAITS */}
          <div className="bvd-results-columns">
            {/* Left column - PERSONA */}
            <div className="bvd-results-column">
              <h3 className="bvd-results-column-title">PERSONA</h3>
              <p className="bvd-results-persona-text">
                {finalPersona.introduction || finalPersona.explainer}
              </p>
            </div>

            {/* Right column - KEY TRAITS */}
            <div className="bvd-results-column">
              <h3 className="bvd-results-column-title">KEY TRAITS:</h3>
              <div className="bvd-results-traits-container">
                {finalPersona.traits && finalPersona.traits.slice(0, 5).map((trait, idx) => (
                  <div key={idx} className="bvd-results-trait-row">
                    <span className="bvd-results-trait-label">{trait.name}:</span>
                    <span className="bvd-results-trait-value">{trait.description}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bvd-results-actions">
            <button
              className="bvd-secondary-button"
              onClick={handleRetake}
            >
              Retake Assessment
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bvd-container">
      {screen === 'initializing' && (
        <div className="bvd-loading-screen">
          <div className="bvd-loading-spinner"></div>
        </div>
      )}

      {screen === 'url-input' && (
        <div className="bvd-url-input-screen">
          <div className="bvd-header">
            <h1>Brand Voice Designer</h1>
            <p className="bvd-subtitle">
              Design your brand's true voice by taking this guided journey.
            </p>
            {!selectedBrand && (
              <div className="bvd-error" style={{ marginTop: '1rem' }}>
                Please select a brand from the selector above to continue.
              </div>
            )}
          </div>

          <div className="bvd-form-card">
            <div className="bvd-input-container">
              <label htmlFor="user-name" className="bvd-label">
                YOUR NAME
              </label>
              <input
                id="user-name"
                type="text"
                className="bvd-url-input"
                placeholder="Enter your name"
                value={userName}
                onChange={(e) => {
                  setUserName(e.target.value);
                  setError(null);
                }}
                autoComplete="off"
                data-1p-ignore="true"
                data-lpignore="true"
                data-bwignore="true"
                data-form-type="other"
              />
            </div>

            <div className="bvd-input-container">
              <label htmlFor="brand-name" className="bvd-label">
                BRAND NAME
              </label>
              <input
                id="brand-name"
                type="text"
                className="bvd-url-input"
                placeholder="Enter brand name"
                value={brandName}
                onChange={(e) => {
                  setBrandName(e.target.value);
                  setError(null);
                }}
                autoComplete="off"
                data-1p-ignore="true"
                data-lpignore="true"
                data-bwignore="true"
                data-form-type="other"
              />
            </div>

            <div className="bvd-input-container">
              <label htmlFor="brand-url" className="bvd-label">
                WEBSITE URL
              </label>
              <input
                id="brand-url"
                type="url"
                className="bvd-url-input"
                placeholder="https://yourbrand.com/about"
                value={urlInput}
                onChange={(e) => {
                  setUrlInput(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAnalyzeUrl();
                  }
                }}
                autoComplete="off"
                data-1p-ignore="true"
                data-lpignore="true"
                data-bwignore="true"
                data-form-type="other"
              />
            </div>

            {error && <div className="bvd-error">{error}</div>}

            <button
              className="bvd-primary-button"
              onClick={handleAnalyzeUrl}
              disabled={isExtractingUrls || !selectedBrand}
            >
              {isExtractingUrls ? 'Analyzing Website...' : 'Start Your Journey'}
            </button>
          </div>
        </div>
      )}

      {screen === 'url-selection' && (
        <div className="bvd-url-input-screen">
          <div className="bvd-header">
            <h1>Choose Your Page</h1>
            <p className="bvd-subtitle">
              Which page from <strong>{urlInput}</strong> would you like to use for the Brand Voice Designer?
            </p>
          </div>

          <div className="bvd-form-card">
            <div className="bvd-url-selection-grid">
              {suggestedUrls.map((urlObj, index) => (
                <button
                  key={index}
                  className="bvd-url-selection-button"
                  onClick={() => handleUrlSelection(urlObj.url, urlObj.title)}
                >
                  <div className="bvd-url-title">{urlObj.title}</div>
                  <div className="bvd-url-text">{urlObj.url}</div>
                </button>
              ))}
            </div>

            <button
              className="bvd-secondary-button"
              onClick={() => setScreen('url-input')}
              style={{ marginTop: '1rem' }}
            >
              Back
            </button>
          </div>
        </div>
      )}

      {screen === 'loading' && renderLoadingScreen()}

      {screen === 'tournament' && renderTournamentScreen()}

      {screen === 'results' && renderResultsScreen()}
    </div>
  );
};

export default BrandVoiceDesigner;
